import React, { useMemo, useState, useEffect } from 'react';
import { message } from 'antd-v5';
import PageHeaderLayout from '@/component/page-header-layout';
import { Button, Table, Card, Modal, InputNumber, Form } from '@/components/index';
import './style.less';
import { getHealthWeightRequest, updateHealthWeightRequest } from '@/state/request/healthWeight';
import { STALL_TYPE } from '@/utils/deviceType';

// 权重项名称
const WEIGHT_LABELS = {
  createOrder: '开单活跃',
  shareOrder: '分享账单',
  repayment: '买家还款',
  stockOperation: '库存管理',
  supplierPay: '货主付款',
};

/**
 * 将权重配置对象格式化为展示字符串（顿号分隔）
 */
function formatWeightSettings(weights) {
  if (!weights || typeof weights !== 'object') return '--';
  const parts = [
    [WEIGHT_LABELS.createOrder, weights.createOrder],
    [WEIGHT_LABELS.shareOrder, weights.shareOrder],
    [WEIGHT_LABELS.repayment, weights.repayment],
    [WEIGHT_LABELS.stockOperation, weights.stockOperation],
    [WEIGHT_LABELS.supplierPay, weights.supplierPay],
  ];
  return parts.map(([label, value]) => `${label}${value ?? 0}%`).join('、');
}

const HealthWeightSetting = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  useEffect(() => {
    getHealthWeight();
  }, []);

  const getHealthWeight = async () => {
    setLoading(true);
    const res = await getHealthWeightRequest();
    setLoading(false);
    if (res?.code === 200) {
      setDataSource(res.data);
    }
  };
  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue(record.healthWeightItemVO || {});
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (!editingRecord) return;
      const total = Object.entries(WEIGHT_LABELS).reduce((acc, [name]) => acc + values[name], 0);
      if (total !== 100) {
        message.error('权重之和必须等于100%');
        return;
      }
      const res = await updateHealthWeightRequest({
        id: editingRecord.id,
        healthWeightItemVO: { ...values },
      });
      if (res?.code === 200) {
        message.success('保存成功');
        setModalVisible(false);
        setEditingRecord(null);
        getHealthWeight();
      }
    } catch (e) {
      // 校验失败不关闭弹窗
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const columns = useMemo(
    () => [
      {
        title: '版本名称',
        dataIndex: 'code',
        key: 'code',
        render: (text) => {
          return STALL_TYPE.get(parseFloat(text));
        },
      },
      {
        title: '权重设置',
        dataIndex: 'healthWeightItemVO',
        key: 'healthWeightItemVO',
        render: (weights) => formatWeightSettings(weights),
      },
      {
        title: '操作',
        key: 'action',
        width: 72,
        render: (_, record) => (
          <span style={{ color: '#01B755', cursor: 'pointer' }} onClick={() => handleEdit(record)}>
            编辑
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <PageHeaderLayout>
      <Card>
        <Table
          rowKey='id'
          loading={loading}
          columns={columns}
          dataSource={dataSource}
          pagination={false}
        />
      </Card>

      <Modal
        title={
          editingRecord ? `${STALL_TYPE.get(parseFloat(editingRecord.code))}权重设置` : '权重设置'
        }
        open={modalVisible}
        onCancel={handleModalCancel}
        footer={
          <div className='health-weight-modal-footer'>
            <Button onClick={handleModalCancel}>取消</Button>
            <Button type='primary' onClick={handleModalOk}>
              确定
            </Button>
          </div>
        }
        destroyOnClose
        width={480}
        maskClosable={false}
      >
        <Form
          form={form}
          style={{ padding: '12px 0', marginBottom: -16 }}
          labelCol={{ flex: '72px' }}
          wrapperCol={{ flex: 1 }}
          requiredMark={false}
          labelAlign='left'
          colon={false}
        >
          {Object.entries(WEIGHT_LABELS).map(([name, label]) => (
            <Form.Item
              key={name}
              name={name}
              label={label}
              rules={[
                { required: true, message: `输入${label}权重` },
                { type: 'number', min: 0, max: 100, message: '输入 0～100 的整数' },
              ]}
            >
              <InputNumber
                min={0}
                max={100}
                precision={0}
                suffix='%'
                placeholder={`输入权重`}
                style={{ width: '100%' }}
                onFocus={(e) => {
                  e.target.select();
                }}
              />
            </Form.Item>
          ))}
        </Form>
      </Modal>
    </PageHeaderLayout>
  );
};

export default HealthWeightSetting;
